/**
 * Message Button Component
 * Used across the app to start conversations with users
 * Updated to allow messaging without connection requirements
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, Loader } from 'lucide-react';
import { useAuth } from '../../../core/contexts/AuthContext';
import connectionService from '../../connect/services/connectionService';
import { toast } from 'react-hot-toast';

/**
 * Message Button Component
 * 
 * @param {Object} props - Component props
 * @param {string} props.userId - User ID to message
 * @param {string} props.variant - Button variant (primary, secondary, small)
 * @param {boolean} props.showText - Whether to show text label
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} Message button component
 */
const MessageButton = ({ userId, variant = 'primary', showText = true, className = '' }) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  // Get button styles based on variant
  const getButtonStyles = () => {
    switch (variant) {
      case 'primary':
        return 'bg-blue-500 hover:bg-blue-600 text-white';
      case 'secondary':
        return 'border border-blue-500 text-blue-500 hover:bg-blue-50';
      case 'small':
        return 'bg-blue-500 hover:bg-blue-600 text-white text-xs px-2 py-1';
      default:
        return 'bg-blue-500 hover:bg-blue-600 text-white';
    }
  };

  // Handle starting conversation - Now works without connection requirement
  const handleStartConversation = async () => {
    if (!currentUser) {
      toast.error('You need to be logged in to send messages');
      return;
    }

    if (userId === currentUser.uid) {
      toast.error('You cannot message yourself');
      return;
    }

    try {
      setIsLoading(true);
      
      // Use the updated connection service that allows messaging anyone
      await connectionService.startConversationWithUser(userId, navigate);
      
    } catch (error) {
      console.error('Error starting conversation:', error);
      toast.error(error.message || 'Could not start conversation');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      onClick={handleStartConversation}
      disabled={isLoading}
      className={`rounded-md flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${getButtonStyles()} ${className}`}
    >
      {isLoading ? (
        <Loader size={showText ? 16 : 20} className="animate-spin" />
      ) : (
        <>
          <MessageSquare size={showText ? 16 : 20} className={showText ? 'mr-1.5' : ''} />
          {showText && <span>Message</span>}
        </>
      )}
    </button>
  );
};

export default MessageButton;